<template>
    <h3>{{ title }}</h3>
    <hr/>
    <DropZone
        :maxFiles="1"
        :headers="headers"
        :acceptedFiles="['xlsx', 'csv', 'xls']"
        :url="API_SERVER"
        :uploadOnDrop="true"
        @sending="sending"
        @removedFile="onFileRemove"
        :maxFileSize="50000000" />
    <br/>
    <p class="p-text-center" v-if="fileId.length && !data">One moment, your file is being uploaded.</p>
    <div class="row" v-if="data">
      <div class="col">
         <p-dropdown
            class="w-full"
            v-model="this.selectedSheet"
            :options="sheetNames"
            placeholder="Select Sheet to use"
            @change="setColumns()" 
          />
      </div>
    </div>
    <br/>
    <div v-if="data && selectedSheet.length && columnOptions">
      <p class="text-muted">Pick the corresponding column for each value.</p>

      <div v-for="field of columns" :key="field.name" class="mb-3">
        <label  class="form-label">{{ field.name }} <span v-if="field.required">(required)</span></label>
       
        <p-dropdown
          v-model="field.targetColumn"
          :options="columnOptions"
          optionLabel="label"
          filter
          class="w-full"
          optionValue="index"
          placeholder="Select corresponding column"
          showClear
        />
      </div>
      <br/>
      <p v-if="selectedSheet">{{ data[selectedSheet].length - 1 }} Rows</p>
      <p-button v-if="invalidData" :disabled="disableBtn"  style="float:right" @click="upload(true)">Force Upload</p-button>
      <p-button :disabled="disableBtn" @click="upload()">Upload</p-button>
     
    </div>


</template>

<script lang="ts">
/* eslint-disable */
import { Options } from 'vue-class-component';
import { 
  ImportColumn,
  APIClient
} from '@/components/api'; 
import { base } from '@/misc/base';
import getBrowserFingerprint from 'get-browser-fingerprint';
import Cookies from 'js-cookie';

@Options({
  props: {
    columns : [],
    title : "",
    apiResourceName: "",
    validate : {} as any,
    postProcess : Function,
  },
  emits : {
    close : Function
  }
})
export default class BulkUploader extends base {
  API_SERVER =  process.env.VUE_APP_API_URL + '/process_spreadsheet';
  API_DL = 'download_spreadsheet'
  columns! : ImportColumn[]
  title! : string
  apiResourceName!: string;
  selectedSheet = ""
  headers : any = { 
    device: getBrowserFingerprint({
      enableWebgl: true,
    })
  }
  
  data: any = null
  sheetNames: string[] = []
  columnOptions: any[] = []
  disableBtn = false
  invalidData = false
  validate! : any
  postProcess!: any
  fileId = ""
  service! : APIClient


  public onFileRemove(){
    this.data = null
    this.$forceUpdate()
  }

  public setColumns(){
    const sheetData = this.data[this.selectedSheet][0]

    this.disableBtn = false

    const selectionOptions = []

    for(let i = 0; i < sheetData.length;i++){
      selectionOptions.push({index: i, label: sheetData[i]})
    }

    this.columnOptions = selectionOptions
    this.$forceUpdate()
  }

  public async upload(force = false) : Promise<void> {
    const sheetData = this.data[this.selectedSheet]

    this.invalidData = false
    this.disableBtn = true

    const anyEmpty = this.columns.filter(
      el => el.required && el.targetColumn == -1,
    )

    if(anyEmpty.length){
      alert("Please fill out all of the required ields.")
      return 
    }
    let result = []

    for(let i = 1; i < sheetData.length;i++){
      let row : any = {}
      let sheetRow = sheetData[i]
      
      for (let j = this.columns.length - 1; j >= 0; j--) {
        let col = this.columns[j]
        let element = sheetRow[col.targetColumn as any]
        
        if(!element)
          continue
          
        if(col.boolType){
          let val = sheetRow[col.targetColumn as any]?.toLowerCase()
          row[col.column] = val == "true" || val == "yes" ? true : false
          continue
        }

        if(col.dateType){
          const vd = sheetRow[col.targetColumn as any]
          if(!vd)
            continue
          let val = new Date(vd)
          row[col.column] = val
          continue
        }

        if(col.numType){
          let val = parseInt(sheetRow[col.targetColumn as any])
          row[col.column] = val
          continue
        }
        if(col.comArray){
          const arr = sheetRow[col.targetColumn as any]
          
          if (!arr){
             continue
          }
          row[col.column] = arr.split(",")
          continue
        }
        
        row[col.column] = sheetRow[col.targetColumn as any]?.trim()

      }

      result.push(row)
    }

    if(this.validate && !force){

      if(!this.validate(result)){
        this.disableBtn = false
        this.invalidData = true
        return
      }
    }

    if(this.postProcess){
      result = this.postProcess(result)
     
    }

   
   if(!this.apiResourceName?.length)
    return
   
   try {
      // For leads, post each lead individually since there's no bulk endpoint
      if (this.apiResourceName === 'lead') {
        const errors = []
        let successCount = 0

        for (const lead of result) {
          try {
            await this.service.post('/leads', lead)
            successCount++
          } catch (e: any) {
            errors.push(`Lead ${lead.customer_name || 'Unknown'}: ${e.response?.data?.error || e.message}`)
          }
        }

        if (successCount > 0) {
          alert(`Successfully uploaded ${successCount} leads.${errors.length > 0 ? ` ${errors.length} failed.` : ''}`)

          // Auto-assign the uploaded leads if requested
          if (confirm('Do you want to auto-assign these leads to field officers?')) {
            try {
              await this.service.post('/managers/lead/auto_reassign', {})
              alert('Leads have been auto-assigned to available field officers.')
            } catch (e) {
              console.log('Auto-assign failed:', e)
              alert('Auto-assignment failed. Please use the Auto Assign button on the leads page.')
            }
          }
        }

        if (errors.length > 0 && errors.length === result.length) {
          throw new Error('All leads failed to upload')
        }
      } else {
        // For other resources, use the standard upload endpoint
        await this.service.post(
          `${this.apiResourceName}/upload`,
          result,
        )
        alert("Upload complete, please wait for the data to reflect.")
      }

      this.disableBtn = true
      this.$forceUpdate();
      this.$emit("close")

    } catch (e : any) {
      console.log(e)
      let msg = e.response?.data?.error || e.message
      this.disableBtn = false
      alert("Failed to upload, please try again : " + msg)
    }
  }

  mounted(){
    this.service =  this.store.state.simforce as any

      let token = Cookies.get("API_TOKEN");
      let tokenSource = "cookie";

      // If no cookie token, try localStorage
      if (!token) {
          token = window.localStorage.getItem("user_token");
          tokenSource = "localStorage";
      }

      // If we found a token in either location
      if (token) {
          // Ensure token has Bearer prefix
          const tokenWithBearer = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

          // Set token in multiple header formats to ensure compatibility
          this.headers["X-TOKEN"] = tokenWithBearer;
          this.headers["Authorization"] = tokenWithBearer;

          // Fix inconsistent casing that might cause CORS issues
          this.headers["x-token"] = tokenWithBearer;
          this.headers["authorization"] = tokenWithBearer;

          console.log(`Token from ${tokenSource} set in headers: ${tokenWithBearer.substring(0, 15)}...`);

          // If token was found in localStorage but not in cookie, restore it to cookie
          // This helps with browser refreshes and cross-tab consistency
          if (tokenSource === "localStorage" && !Cookies.get("API_TOKEN")) {
              console.log("Restoring missing cookie from localStorage token");
              try {
                  Cookies.set("API_TOKEN", token, {
                      path: '/',
                      sameSite: 'strict',
                      secure: window.location.protocol === 'https:'
                  });
              } catch (e) {
                  console.error("Failed to restore API_TOKEN cookie:", e);
              }
          }
      }
  }

  public sending(files : any, xhrReq : any) {

    this.data = null

    const handleUpload = ((response : string) => {
      console.log(response)
      const fileId = JSON.parse(response).id
      this.fileId = fileId
      let timer = setInterval(async () => {
        
        try {

          let res = await this.service
          .get(`${this.API_DL}/${fileId}`)

          this.data = res.data.result

          this.sheetNames = Object.keys(this.data)
          this.$forceUpdate()
          clearInterval(timer)
        } catch(e){
          //exception, data
          // is not ready
          console.log("Data not ready!")
        }

      }, 5 * 1000);

      
    }).bind(this)

    xhrReq.onreadystatechange = function() {
        if (xhrReq.readyState == XMLHttpRequest.DONE) {
             
            handleUpload(xhrReq.responseText)
        }
    }
  
  }

}
</script>
<style scoped type="text/css">
.list-group {
  margin-top: 10px;
}

.form-select {
  display: block;
  width: 100%;
}
</style>

